import React, { useState, useEffect, useRef } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { CheckCircle, MessageSquare, MapPin, Cpu, ShieldCheck, Sparkles } from "lucide-react";

const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" }, { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" }, { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" }
];

const CA_PROVINCES = [
  { value: "AB", label: "Alberta" }, { value: "BC", label: "British Columbia" }, { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" }, { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" }, { value: "ON", label: "Ontario" }, { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" }, { value: "SK", label: "Saskatchewan" }, { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" }, { value: "YT", label: "Yukon" }
];

const US_INDUSTRIES = [
  { value: "Agriculture, Forestry, Fishing and Hunting", label: "Agriculture, Forestry, Fishing and Hunting" },
  { value: "Mining, Quarrying, and Oil and Gas Extraction", label: "Mining, Quarrying, and Oil and Gas Extraction" },
  { value: "Construction", label: "Construction" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Wholesale Trade", label: "Wholesale Trade" },
  { value: "Retail Trade", label: "Retail Trade" },
  { value: "Transportation and Warehousing", label: "Transportation and Warehousing" },
  { value: "Utilities", label: "Utilities" },
  { value: "Information", label: "Information" },
  { value: "Finance and Insurance", label: "Finance and Insurance" },
  { value: "Real Estate and Rental and Leasing", label: "Real Estate and Rental and Leasing" },
  { value: "Professional, Scientific, and Technical Services", label: "Professional, Scientific, and Technical Services" },
  { value: "Management of Companies and Enterprises", label: "Management of Companies and Enterprises" },
  { value: "Administrative and Support and Waste Management Services", label: "Administrative and Support and Waste Management Services" },
  { value: "Educational Services", label: "Educational Services" },
  { value: "Health Care and Social Assistance", label: "Health Care and Social Assistance" },
  { value: "Arts, Entertainment, and Recreation", label: "Arts, Entertainment, and Recreation" },
  { value: "Accommodation and Food Services", label: "Accommodation and Food Services" },
  { value: "Other Services (except Public Administration)", label: "Other Services (except Public Administration)" },
  { value: "Public Administration", label: "Public Administration" },
  { value: "Technology", label: "Technology" },
  { value: "Automotive", label: "Automotive" },
  { value: "Aerospace", label: "Aerospace" },
  { value: "Biotechnology", label: "Biotechnology" },
  { value: "Pharmaceuticals", label: "Pharmaceuticals" },
  { value: "Energy", label: "Energy" },
  { value: "Food and Beverage", label: "Food and Beverage" },
  { value: "Textiles", label: "Textiles" },
  { value: "Chemical", label: "Chemical" },
  { value: "Paper and Forest Products", label: "Paper and Forest Products" },
  { value: "Steel and Metals", label: "Steel and Metals" },
  { value: "Machinery", label: "Machinery" },
  { value: "Electronics", label: "Electronics" },
  { value: "Telecommunications", label: "Telecommunications" },
  { value: "Internet and Software", label: "Internet and Software" },
  { value: "Media and Entertainment", label: "Media and Entertainment" },
  { value: "Gaming", label: "Gaming" },
  { value: "E-commerce", label: "E-commerce" },
  { value: "Consulting", label: "Consulting" },
  { value: "Legal Services", label: "Legal Services" },
  { value: "Accounting", label: "Accounting" },
  { value: "Marketing and Advertising", label: "Marketing and Advertising" },
  { value: "Architecture and Engineering", label: "Architecture and Engineering" },
  { value: "Design", label: "Design" },
  { value: "Research and Development", label: "Research and Development" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "Medical Devices", label: "Medical Devices" },
  { value: "Dental", label: "Dental" },
  { value: "Veterinary", label: "Veterinary" },
  { value: "Fitness and Wellness", label: "Fitness and Wellness" },
  { value: "Beauty and Personal Care", label: "Beauty and Personal Care" },
  { value: "Education", label: "Education" },
  { value: "Training and Development", label: "Training and Development" },
  { value: "Childcare", label: "Childcare" },
  { value: "Elder Care", label: "Elder Care" },
  { value: "Non-profit", label: "Non-profit" },
  { value: "Religious Organizations", label: "Religious Organizations" },
  { value: "Sports", label: "Sports" },
  { value: "Tourism and Travel", label: "Tourism and Travel" },
  { value: "Hospitality", label: "Hospitality" },
  { value: "Event Planning", label: "Event Planning" },
  { value: "Catering", label: "Catering" },
  { value: "Cleaning Services", label: "Cleaning Services" },
  { value: "Security Services", label: "Security Services" },
  { value: "Landscaping", label: "Landscaping" },
  { value: "HVAC", label: "HVAC" },
  { value: "Plumbing", label: "Plumbing" },
  { value: "Electrical", label: "Electrical" },
  { value: "Carpentry", label: "Carpentry" },
  { value: "Roofing", label: "Roofing" },
  { value: "Painting", label: "Painting" },
  { value: "Flooring", label: "Flooring" },
  { value: "Home Improvement", label: "Home Improvement" },
  { value: "Pest Control", label: "Pest Control" },
  { value: "Moving Services", label: "Moving Services" },
  { value: "Storage", label: "Storage" },
  { value: "Logistics", label: "Logistics" },
  { value: "Shipping", label: "Shipping" },
  { value: "Freight", label: "Freight" },
  { value: "Public Transportation", label: "Public Transportation" },
  { value: "Taxi and Rideshare", label: "Taxi and Rideshare" },
  { value: "Delivery Services", label: "Delivery Services" },
  { value: "Postal Services", label: "Postal Services" },
  { value: "Insurance", label: "Insurance" },
  { value: "Banking", label: "Banking" },
  { value: "Investment", label: "Investment" },
  { value: "Credit Services", label: "Credit Services" },
  { value: "Mortgage", label: "Mortgage" },
  { value: "Property Management", label: "Property Management" },
  { value: "Equipment Rental", label: "Equipment Rental" },
  
  { value: "Other", label: "Other" }
];

const COUNTRY_OPTIONS = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" }
];

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validatePhone(phone) {
  return /^\d{10}$/.test(phone.replace(/\D/g, ""));
}

function Signup() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    organization: "",
    orgType: null,
    address: "",
    city: "",
    state: null,
    zip: "",
    country: COUNTRY_OPTIONS[0],
    email: "",
    phone: "",
    companyWebsite: ""
  });
  
  const [orgTypes, setOrgTypes] = useState(US_INDUSTRIES);
  const [stateOptions, setStateOptions] = useState(US_STATES);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const recaptchaRef = useRef();
  const navigate = useNavigate();
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (form.country && form.country.value === "US") setStateOptions(US_STATES);
    else setStateOptions(CA_PROVINCES);
    setForm(f => ({ ...f, state: null }));
    // eslint-disable-next-line
  }, [form.country]);

  const loadOrgTypes = (inputValue, callback) => {
    const filtered = orgTypes.filter(type =>
      type.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered);
  };

  const loadStates = (inputValue, callback) => {
    const filtered = stateOptions.filter(state =>
      state.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setForm({ ...form, [name]: value });
  };

  const handleOrgTypeCreate = (inputValue) => {
    const newType = { value: inputValue, label: inputValue };
    setOrgTypes([...orgTypes, newType]);
    setForm({ ...form, orgType: newType });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validation
    if (
      !form.firstName ||
      !form.lastName ||
      !form.organization ||
      !form.orgType ||
      !form.address ||
      !form.city ||
      !form.state ||
      !form.zip ||
      !form.country ||
      !form.email ||
      !form.phone
    ) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }
    if (!validateEmail(form.email)) {
      setError("Invalid email address.");
      setLoading(false);
      return;
    }
    if (!validatePhone(form.phone)) {
      setError("Invalid phone number. Enter 10 digits.");
      setLoading(false);
      return;
    }
    if (!captchaToken) {
      setError("Please complete the CAPTCHA.");
      setLoading(false);
      return;
    }

    // Prepend +1 for US or Canada phone numbers
    let phoneToSend = form.phone;
    if (
      (form.country.value === "US" || form.country.value === "CA") &&
      !form.phone.startsWith("+")
    ) {
      phoneToSend = "+1" + form.phone;
    }

    try {
      const addressString = `${form.address}, ${form.city}, ${form.state ? form.state.label : ""}, ${form.zip}, ${form.country ? form.country.label : ""}`;

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/signup/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.firstName,
          last_name: form.lastName,
          organization: form.organization,
          organization_type: form.orgType.value,
          address: addressString,
          email: form.email,
          phone_number: phoneToSend,
          company_website_url: form.companyWebsite,
          captcha: captchaToken
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || "Signup failed. Please try again.");
        setLoading(false);
        return;
      }

      setUserEmail(form.email);
      setSuccess(true);
      setLoading(false);
    } catch (err) {
      setError("Signup failed. Please try again.");
      setLoading(false);
    }
  };

  // Success page
  if (success) {
    return (
      <section className="cp-hero">
        <div className="cp-container" style={{ maxWidth: 540, textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 72, height: 72, borderRadius: "50%",
            background: "rgba(16, 185, 129, 0.12)", color: "#047857",
            marginBottom: 24,
          }}>
            <CheckCircle size={36} />
          </div>
          <h1 className="cp-h2" style={{ fontSize: "var(--fs-h1)" }}>Check your inbox.</h1>
          <p className="cp-lede" style={{ margin: "12px auto 32px" }}>
            We've emailed a verification link to <strong>{userEmail}</strong>. Click it
            to activate your account.
          </p>
          <button className="cp-btn cp-btn--primary cp-btn--lg" onClick={() => navigate("/login")}>
            Go to sign in
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="cp-hero" style={{ paddingBottom: 96 }}>
      <div className="cp-container cp-hero-grid" style={{ alignItems: "start" }}>
        {/* Left Column - Marketing */}
        <div>
          <span className="cp-eyebrow">
            <Sparkles size={12} style={{ verticalAlign: "-1px", marginRight: 6 }} />
            Start your free workspace
          </span>
          <h1 className="cp-h1" style={{ marginTop: 20 }}>
            Plan smarter. Communicate faster.
          </h1>
          <p className="cp-lede" style={{ marginBottom: 32 }}>
            Spin up your CoPlaid workspace and connect your equipment, your teams and your
            communication on day one — connected devices when you're ready.
          </p>

          <div style={{ display: "grid", gap: 12 }}>
            <div className="cp-card" style={{ padding: 20, display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: "var(--brand-50)", color: "var(--brand-600)",
                display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <MapPin size={18} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: "var(--ink-700)", marginBottom: 2 }}>
                  The platform
                </div>
                <div style={{ color: "var(--ink-500)", fontSize: 14 }}>
                  Agents that judge what your equipment reports, beside the equipment.
                </div>
              </div>
            </div>
            <div className="cp-card" style={{ padding: 20, display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: "var(--brand-50)", color: "var(--brand-600)",
                display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <MessageSquare size={18} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: "var(--ink-700)", marginBottom: 2 }}>
                  Communication
                </div>
                <div style={{ color: "var(--ink-500)", fontSize: 14 }}>
                  Reach the right person on a channel they already use, and hear back.
                </div>
              </div>
            </div>
            <div className="cp-card" style={{ padding: 20, display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: "var(--brand-50)", color: "var(--brand-600)",
                display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <Cpu size={18} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: "var(--ink-700)", marginBottom: 2 }}>
                  Embedded Intelligence
                </div>
                <div style={{ color: "var(--ink-500)", fontSize: 14 }}>
                  Connect devices and trigger workflows from real-world signals.
                </div>
              </div>
            </div>

            <div style={{
              display: "flex", gap: 16, alignItems: "center",
              padding: "12px 16px",
              background: "rgba(16, 185, 129, 0.06)",
              border: "1px solid rgba(16, 185, 129, 0.18)",
              borderRadius: "var(--radius-md)",
              color: "var(--ink-500)",
              fontSize: 14,
              marginTop: 8,
            }}>
              <ShieldCheck size={18} color="#047857" />
              <span><strong style={{ color: "var(--ink-700)" }}>Free to start.</strong> No credit card required.</span>
            </div>
          </div>
        </div>

          {/* Right Column - Signup Form */}
          <div className="cp-card signup-right-section" style={{ padding: 32 }}>
            <div className="signup-form-card" style={{ boxShadow: "none", border: "none", padding: 0 }}>
              <h2 className="signup-form-title">Create your CoPlaid account</h2>
              {error && <div className="signup-error">{error}</div>}
              
              <form onSubmit={handleSubmit}>
                <div className="signup-form-grid">
                  <div className="signup-form-group">
                    <label className="signup-label">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      className="signup-input"
                      required
                    />
                  </div>

                  <div className="signup-form-group">
                    <label className="signup-label">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      className="signup-input"
                      required
                    />
                  </div>

                  <div className="signup-form-group signup-form-group-full">
                    <label className="signup-label">Organization *</label>
                    <input
                      type="text"
                      name="organization"
                      value={form.organization}
                      onChange={handleChange}
                      className="signup-input"
                      required
                    />
                  </div>

                  <div className="signup-form-group signup-form-group-full">
                    <label className="signup-label">Industry *</label>
                    <div className="signup-react-select">
                      <AsyncCreatableSelect
                        cacheOptions
                        defaultOptions={orgTypes}
                        loadOptions={loadOrgTypes}
                        onChange={value => handleSelectChange("orgType", value)}
                        onCreateOption={handleOrgTypeCreate}
                        value={form.orgType}
                        isClearable
                        placeholder="Select or type industry"
                        classNamePrefix="react-select"
                      />
                    </div>
                  </div>

                  <div className="signup-form-group signup-form-group-full">
                    <label className="signup-label">Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      className="signup-input"
                      placeholder="Street address"
                      required
                    />
                  </div>

                  <div className="signup-form-group">
                    <label className="signup-label">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      className="signup-input"
                      required
                    />
                  </div>

                  <div className="signup-form-group">
                    <label className="signup-label">State/Province *</label>
                    <div className="signup-react-select">
                      <AsyncCreatableSelect
                        cacheOptions
                        defaultOptions={stateOptions}
                        loadOptions={loadStates}
                        onChange={value => handleSelectChange("state", value)}
                        value={form.state}
                        isClearable
                        placeholder="Select state/province"
                        isDisabled={!form.country}
                        classNamePrefix="react-select"
                      />
                    </div>
                  </div>

                  <div className="signup-form-group">
                    <label className="signup-label">Zip Code *</label>
                    <input
                      type="text"
                      name="zip"
                      value={form.zip}
                      onChange={handleChange}
                      className="signup-input"
                      required
                    />
                  </div>

                  <div className="signup-form-group">
                    <label className="signup-label">Country *</label>
                    <select
                      name="country"
                      value={form.country.value}
                      onChange={e => {
                        const selected = COUNTRY_OPTIONS.find(c => c.value === e.target.value);
                        handleSelectChange("country", selected);
                      }}
                      className="signup-select"
                      required
                    >
                      {COUNTRY_OPTIONS.map(c => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="signup-form-group signup-form-group-full">
                    <label className="signup-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="signup-input"
                      required
                    />
                  </div>

                  <div className="signup-form-group">
                    <label className="signup-label">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      className="signup-input"
                      placeholder="10 digits"
                      required
                    />
                  </div>

                  <div className="signup-form-group">
                    <label className="signup-label">Website</label>
                    <input
                      type="url"
                      name="companyWebsite"
                      value={form.companyWebsite}
                      onChange={handleChange}
                      className="signup-input"
                      placeholder="https://..."
                    />
                  </div>

                  <div className="signup-form-group signup-form-group-full">
                    <div className="signup-recaptcha">
                      <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        size="normal"
                        ref={recaptchaRef}
                        onChange={token => setCaptchaToken(token)}
                      />
                    </div>
                  </div>
                </div>

                {/* Consent & Compliance */}
                <div className="signup-consent">
  <label className="signup-consent-checkbox">
    <input
      type="checkbox"
      checked={termsAccepted}
      onChange={(e) => setTermsAccepted(e.target.checked)}
      className="signup-checkbox-input"
    />
    <span className="signup-checkbox-custom"></span>
    <p className="signup-consent-text">
      By creating an account, you agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
      <br />
      If you enable messaging, you consent to receive operational messages; message and data rates may apply. Reply STOP to opt out.
    </p>
  </label>
</div>

                <button type="submit" disabled={loading} className="signup-submit-btn">
                  {loading ? "Creating Account..." : "Create Account"}
                </button>
              </form>
            </div>
          </div>
      </div>
    </section>
  );
}

export default Signup;